import type { ZodSimplify } from '@mntn-dev/utility-types'
import type { EmptyObject, OverrideProperties, SimplifyDeep } from 'type-fest'
import { z } from 'zod'
import {
  type ActivityDomainSelectModel,
  ActivityDomainSelectModelSchema,
  type ActivityType,
} from './activity.models.ts'
import {
  type AgreementDomainSelectModel,
  AgreementDomainSelectModelSchema,
} from './agreement.models.ts'
import {
  type BidDomainSelectModel,
  BidDomainSelectModelSchema,
} from './bid.models.ts'
import {
  type BillingProfileDomainSelectModel,
  BillingProfileDomainSelectModelSchema,
} from './billing-profile.models.ts'
import {
  type DeliverableDomainSelectModel,
  DeliverableDomainSelectModelSchema,
} from './deliverable.models.ts'
import {
  type FileDomainSelectModel,
  FileDomainSelectModelSchema,
} from './file.models.ts'
import {
  type LocationDomainSelectModel,
  LocationDomainSelectModelSchema,
} from './location.models.ts'
import {
  type NotificationDomainSelectModel,
  NotificationDomainSelectModelSchema,
} from './notification.models.ts'
import {
  type OfferDomainSelectModel,
  OfferDomainSelectModelSchema,
} from './offer.models.ts'
import {
  type OrganizationDomainSelectModel,
  OrganizationDomainSelectModelSchema,
  type OrganizationType,
} from './organization.models.ts'
import {
  type PackageServiceDomainSelectModel,
  PackageServiceDomainSelectModelSchema,
} from './package-service.models.ts'
import {
  type PackageDomainSelectModel,
  PackageDomainSelectModelSchema,
} from './package.models.ts'
import {
  type PartnerProgramDomainSelectModel,
  PartnerProgramDomainSelectModelSchema,
} from './partner-program.models.ts'
import {
  type ProjectServiceDomainSelectModel,
  ProjectServiceDomainSelectModelSchema,
} from './project-service.models.ts'
import {
  type ProjectViewDomainSelectModel,
  ProjectViewDomainSelectModelSchema,
} from './project.models.ts'
import {
  type ProofDomainSelectModel,
  ProofDomainSelectModelSchema,
} from './proof.models.ts'
import {
  type ReviewDomainSelectModel,
  ReviewDomainSelectModelSchema,
} from './review.models.ts'
import {
  type RoundDomainSelectModel,
  RoundDomainSelectModelSchema,
} from './round.models.ts'
import {
  type ServiceDomainSelectModel,
  ServiceDomainSelectModelSchema,
} from './service.models.ts'
import {
  type SurveyDomainSelectModel,
  SurveyDomainSelectModelSchema,
} from './survey.models.ts'
import {
  type TagDomainSelectModel,
  TagDomainSelectModelSchema,
} from './tag.models.ts'
import {
  type TeamProfileDomainSelectModel,
  TeamProfileDomainSelectModelSchema,
} from './team-profile.models.ts'
import {
  type TeamDomainSelectModel,
  TeamDomainSelectModelSchema,
} from './team.models.ts'
import type { TeamId } from './unique-id.models.ts'
import {
  type UserDomainSelectModel,
  UserDomainSelectModelSchema,
} from './user.models.ts'
import {
  type WatchDomainSelectModel,
  WatchDomainSelectModelSchema,
} from './watch.models.ts'

/**
 * Typescript does not allow circular references between objects so the validation can only
 * be done one level deep which is sufficient for testing and mapping purposes. The relations
 * are defined using .passthrough() so that the validation won't fail when there are nested
 * relations. The following types/functions are used to define the relations for consistency.
 */

/**
 * A helper function to define the relations for a specific query model
 * @param relations The set of relations for a specific query model
 * @returns A schema that does not allow any additional properties and allows the relations to be optional
 */
type Relations<Links> = Partial<Links>

const relations = <Links extends z.ZodRawShape>(links: Links) =>
  z.object(links).partial()

/**
 * A helper function to define a one-to-one relation
 * @param schema The referenced underlying select model schema
 * @returns A schema with .passthrough() applied (so that nested relations won't fail validation)
 *  refined to coerce the parsed value to the specified query model type
 */
type One<To> = To

const one = <SelectModelSchema extends z.ZodRawShape>(
  schema: z.ZodObject<SelectModelSchema>
) => {
  const passthrough = schema.passthrough()
  return {
    as: <QueryModel extends One<ZodSimplify<typeof passthrough>>>() =>
      passthrough.refine((relation): relation is QueryModel => true),
  }
}

/**
 * A helper function to define a one-to-many relation
 * @param schema The referenced underlying select model schema
 * @returns A schema with .passthrough() applied (so that nested relations won't fail validation)
 * refined to coerce the parsed array elements to the specified query model type
 */
type Many<To> = Array<To>

const many = <SelectModelSchema extends z.ZodRawShape>(
  schema: z.ZodObject<SelectModelSchema>
) => {
  const passthrough = schema.passthrough()
  return {
    as: <QueryModel extends Many<ZodSimplify<typeof passthrough>>>() =>
      passthrough.array().refine((relation): relation is QueryModel => true),
  }
}

/**
 * ActivityDomainQueryModel
 */
export const ActivityDomainQueryModelSchema =
  ActivityDomainSelectModelSchema.merge(
    relations({
      actor: one(UserDomainSelectModelSchema).as<One<UserDomainQueryModel>>(),
    })
  )

export type ActivityDomainQueryModel<Type extends ActivityType = ActivityType> =
  ActivityDomainSelectModel<Type> &
    Relations<{
      actor: One<UserDomainQueryModel>
    }>

/**
 * AgreementDomainQueryModel
 */
export const AgreementDomainQueryModelSchema =
  AgreementDomainSelectModelSchema.merge(relations({}))

export type AgreementDomainQueryModel = AgreementDomainSelectModel &
  Relations<EmptyObject>

/**
 * BidDomainQueryModel
 */
export const BidDomainQueryModelSchema = BidDomainSelectModelSchema.merge(
  relations({
    agencyTeam: one(TeamDomainSelectModelSchema).as<
      One<TeamDomainQueryModel>
    >(),
    files: many(FileDomainSelectModelSchema).as<Many<FileDomainQueryModel>>(),
    project: one(ProjectViewDomainSelectModelSchema).as<
      One<ProjectDomainQueryModel>
    >(),
  })
)

export type BidDomainQueryModel = BidDomainSelectModel &
  Relations<{
    agencyTeam: One<TeamDomainQueryModel>
    files: Many<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
  }>

/**
 * BillingProfileDomainQueryModel
 */
export const BillingProfileDomainQueryModelSchema =
  BillingProfileDomainSelectModelSchema.merge(relations({}))

export type BillingProfileDomainQueryModel = BillingProfileDomainSelectModel &
  Relations<{
    organization: One<OrganizationDomainQueryModel>
  }>

/**
 * DeliverableDomainQueryModel
 */
export const DeliverableDomainQueryModelSchema =
  DeliverableDomainSelectModelSchema.merge(
    relations({
      file: one(FileDomainSelectModelSchema).as<One<FileDomainQueryModel>>(),
      project: one(ProjectViewDomainSelectModelSchema).as<
        One<ProjectDomainQueryModel>
      >(),
      service: one(ProjectServiceDomainSelectModelSchema).as<
        One<ProjectServiceDomainQueryModel>
      >(),
    })
  )

export type DeliverableDomainQueryModel = DeliverableDomainSelectModel &
  Relations<{
    file: One<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
  }>

/**
 * FileDomainQueryModel
 */
export const FileDomainQueryModelSchema = FileDomainSelectModelSchema.merge(
  relations({
    owner: one(UserDomainSelectModelSchema).as<One<UserDomainQueryModel>>(),
    project: one(ProjectViewDomainSelectModelSchema).as<
      One<ProjectDomainQueryModel>
    >(),
    service: one(ProjectServiceDomainSelectModelSchema).as<
      One<ProjectServiceDomainQueryModel>
    >(),
    tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
  })
)

export type FileDomainQueryModel = FileDomainSelectModel &
  Relations<{
    owner: One<UserDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * LocationDomainQueryModel
 */
export const LocationDomainQueryModelSchema =
  LocationDomainSelectModelSchema.merge(relations({}))

export type LocationDomainQueryModel = LocationDomainSelectModel &
  Relations<EmptyObject>

/**
 * NotificationDomainQueryModel
 */
export const NotificationDomainQueryModelSchema =
  NotificationDomainSelectModelSchema.merge(
    relations({
      activity: one(ActivityDomainSelectModelSchema).as<
        One<ActivityDomainQueryModel>
      >(),
      recipient: one(UserDomainSelectModelSchema).as<
        One<UserDomainQueryModel>
      >(),
    })
  )

export type NotificationDomainQueryModel = NotificationDomainSelectModel &
  Relations<{
    activity: One<ActivityDomainQueryModel>
    recipient: One<UserDomainQueryModel>
  }>

/**
 * OfferDomainQueryModel
 */
export const OfferDomainQueryModelSchema = OfferDomainSelectModelSchema.merge(
  relations({
    agencyTeam: one(TeamDomainSelectModelSchema).as<
      One<TeamDomainQueryModel>
    >(),
    acceptor: one(UserDomainSelectModelSchema).as<One<UserDomainQueryModel>>(),
    project: one(ProjectViewDomainSelectModelSchema).as<
      One<ProjectDomainQueryModel>
    >(),
  })
)

export type OfferDomainQueryModel = OfferDomainSelectModel &
  Relations<{
    agencyTeam: One<TeamDomainQueryModel>
    acceptor: One<UserDomainQueryModel>
    project: One<ProjectDomainQueryModel>
  }>

/**
 * OrganizationDomainQueryModel
 */
export const OrganizationDomainQueryModelSchema =
  OrganizationDomainSelectModelSchema.merge(
    relations({
      teams: many(TeamDomainSelectModelSchema).as<Many<TeamDomainQueryModel>>(),
      partnerPrograms: many(PartnerProgramDomainSelectModelSchema).as<
        Many<PartnerProgramDomainQueryModel>
      >(),
      users: many(UserDomainSelectModelSchema).as<Many<UserDomainQueryModel>>(),
      billingProfiles: many(BillingProfileDomainSelectModelSchema).as<
        Many<BillingProfileDomainQueryModel>
      >(),
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    })
  )

export type OrganizationDomainQueryModel = OrganizationDomainSelectModel &
  Relations<{
    teams: Many<TeamDomainQueryModel>
    users: Many<UserDomainQueryModel>
    partnerPrograms: Many<PartnerProgramDomainQueryModel>
    billingProfiles: Many<BillingProfileDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * PartnerProgramDomainQueryModel
 */
export const PartnerProgramDomainQueryModelSchema =
  PartnerProgramDomainSelectModelSchema.merge(
    relations({
      organization: one(OrganizationDomainSelectModelSchema).as<
        One<OrganizationDomainQueryModel>
      >(),
    })
  )

export type PartnerProgramDomainQueryModel = PartnerProgramDomainSelectModel &
  Relations<{
    organization: One<OrganizationDomainQueryModel>
  }>

/**
 * PackageDomainQueryModel
 */
export const PackageDomainQueryModelSchema =
  PackageDomainSelectModelSchema.merge(
    relations({
      services: many(PackageServiceDomainSelectModelSchema).as<
        Many<PackageServiceDomainQueryModel>
      >(),
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    })
  )

export type PackageDomainQueryModel = PackageDomainSelectModel &
  Relations<{
    services: Many<PackageServiceDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * PackageServiceDomainQueryModel
 */
export const PackageServiceDomainQueryModelSchema =
  PackageServiceDomainSelectModelSchema.merge(
    relations({
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    })
  )

export type PackageServiceDomainQueryModel = PackageServiceDomainSelectModel &
  Relations<{
    tags: Many<TagDomainQueryModel>
  }>

/**
 * ProjectDomainQueryModel
 */
export const ProjectDomainQueryModelSchema =
  ProjectViewDomainSelectModelSchema.merge(
    relations({
      agencyTeam: one(TeamDomainSelectModelSchema).as<
        One<TeamDomainQueryModel>
      >(),
      acceptor: one(UserDomainSelectModelSchema).as<
        One<UserDomainQueryModel>
      >(),
      brandTeam: one(TeamDomainSelectModelSchema).as<
        One<TeamDomainQueryModel>
      >(),
      owner: one(UserDomainSelectModelSchema).as<One<UserDomainQueryModel>>(),
      activity: many(ActivityDomainSelectModelSchema).as<
        Many<ActivityDomainQueryModel>
      >(),
      services: many(ProjectServiceDomainSelectModelSchema).as<
        Many<ProjectServiceDomainQueryModel>
      >(),
      offers: many(OfferDomainSelectModelSchema).as<
        Many<OfferDomainQueryModel>
      >(),
      files: many(FileDomainSelectModelSchema).as<Many<FileDomainQueryModel>>(),
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
      review: one(ReviewDomainSelectModelSchema).as<
        One<ReviewDomainQueryModel>
      >(),
      deliverables: many(DeliverableDomainSelectModelSchema).as<
        Many<DeliverableDomainQueryModel>
      >(),
      thumbnail: one(FileDomainSelectModelSchema).as<
        One<FileDomainQueryModel>
      >(),
      bids: many(BidDomainSelectModelSchema).as<Many<BidDomainQueryModel>>(),
      package: one(PackageDomainSelectModelSchema).as<
        One<PackageDomainQueryModel>
      >(),
    })
  )

export type ProjectDomainQueryModel = ProjectViewDomainSelectModel &
  Relations<{
    agencyTeam: One<TeamDomainQueryModel>
    acceptor: One<UserDomainQueryModel>
    brandTeam: One<TeamDomainQueryModel>
    owner: One<UserDomainQueryModel>
    activity: Many<ActivityDomainQueryModel>
    services: Many<ProjectServiceDomainQueryModel>
    offers: Many<OfferDomainQueryModel>
    files: Many<FileDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    review: One<ReviewDomainQueryModel>
    deliverables: Many<DeliverableDomainQueryModel>
    thumbnail: One<FileDomainQueryModel>
    bids: Many<BidDomainQueryModel>
    package: One<PackageDomainQueryModel>
  }>

/**
 * ProjectServiceDomainQueryModel
 */
export const ProjectServiceDomainQueryModelSchema =
  ProjectServiceDomainSelectModelSchema.merge(
    relations({
      files: many(FileDomainSelectModelSchema).as<Many<FileDomainQueryModel>>(),
      project: one(ProjectViewDomainSelectModelSchema).as<
        One<ProjectDomainQueryModel>
      >(),
      deliverables: many(DeliverableDomainSelectModelSchema).as<
        Many<DeliverableDomainQueryModel>
      >(),
      review: one(ReviewDomainSelectModelSchema).as<
        One<ReviewDomainQueryModel>
      >(),
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    })
  )

export type ProjectServiceDomainQueryModel = ProjectServiceDomainSelectModel &
  Relations<{
    files: Many<FileDomainQueryModel>
    project: One<ProjectDomainQueryModel>
    deliverables: Many<DeliverableDomainQueryModel>
    review: One<ReviewDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

/**
 * ProofDomainQueryModel
 */
export const ProofDomainQueryModelSchema = ProofDomainSelectModelSchema.merge(
  relations({
    deliverable: one(DeliverableDomainSelectModelSchema).as<
      One<DeliverableDomainQueryModel>
    >(),
    file: one(FileDomainSelectModelSchema).as<One<FileDomainQueryModel>>(),
    round: one(RoundDomainSelectModelSchema).as<One<RoundDomainQueryModel>>(),
  })
)

export type ProofDomainQueryModel = SimplifyDeep<
  ProofDomainSelectModel &
    Relations<{
      deliverable: One<DeliverableDomainQueryModel>
      file: One<FileDomainQueryModel>
      round: One<RoundDomainQueryModel>
    }>
>

/**
 * ReviewDomainQueryModel
 */
export const ReviewDomainQueryModelSchema = ReviewDomainSelectModelSchema.merge(
  relations({
    project: one(ProjectViewDomainSelectModelSchema).as<
      One<ProjectDomainQueryModel>
    >(),
    service: one(ProjectServiceDomainSelectModelSchema).as<
      One<ProjectServiceDomainQueryModel>
    >(),
    rounds: many(RoundDomainSelectModelSchema).as<
      Many<RoundDomainQueryModel>
    >(),
  })
)

export type ReviewDomainQueryModel = ReviewDomainSelectModel &
  Relations<{
    project: One<ProjectDomainQueryModel>
    service: One<ProjectServiceDomainQueryModel>
    rounds: Many<RoundDomainQueryModel>
  }>

/**
 * RoundDomainQueryModel
 */
export const RoundDomainQueryModelSchema = RoundDomainSelectModelSchema.merge(
  relations({
    proofs: many(ProofDomainSelectModelSchema).as<
      Many<ProofDomainQueryModel>
    >(),
  })
).extend({
  proposal: RoundDomainSelectModelSchema.shape.proposal.extend({
    submitted: RoundDomainSelectModelSchema.shape.proposal.shape.submitted
      .unwrap()
      .merge(
        relations({
          actor: one(UserDomainSelectModelSchema).as<
            One<UserDomainQueryModel>
          >(),
        })
      )
      .optional(),
  }),
  feedback: RoundDomainSelectModelSchema.shape.feedback.extend({
    submitted: RoundDomainSelectModelSchema.shape.feedback.shape.submitted
      .unwrap()
      .merge(
        relations({
          actor: one(UserDomainSelectModelSchema).as<
            One<UserDomainQueryModel>
          >(),
        })
      )
      .optional(),
  }),
})

export type RoundDomainQueryModel = SimplifyDeep<
  OverrideProperties<
    RoundDomainSelectModel &
      Relations<{
        proofs: Many<ProofDomainQueryModel>
      }>,
    {
      proposal: OverrideProperties<
        RoundDomainSelectModel['proposal'],
        Partial<{
          submitted: RoundDomainSelectModel['proposal']['submitted'] &
            Relations<{
              actor: One<UserDomainQueryModel>
            }>
        }>
      >
      feedback: OverrideProperties<
        RoundDomainSelectModel['feedback'],
        Partial<{
          submitted: RoundDomainSelectModel['feedback']['submitted'] &
            Relations<{
              actor: One<UserDomainQueryModel>
            }>
        }>
      >
    }
  >
>

/**
 * ServiceDomainQueryModel
 */
export const ServiceDomainQueryModelSchema =
  ServiceDomainSelectModelSchema.merge(
    relations({
      tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    })
  )

export type ServiceDomainQueryModel = ServiceDomainSelectModel &
  Relations<{
    tags: Many<TagDomainQueryModel>
  }>

/**
 * SurveyDomainQueryModel
 */
export const SurveyDomainQueryModelSchema = SurveyDomainSelectModelSchema.merge(
  relations({})
)

export type SurveyDomainQueryModel = SurveyDomainSelectModel &
  Relations<EmptyObject>

/**
 * TagDomainQueryModel
 */
export const TagDomainQueryModelSchema = TagDomainSelectModelSchema.merge(
  relations({})
)

export type TagDomainQueryModel = TagDomainSelectModel & Relations<EmptyObject>

/**
 * TeamDomainQueryModel
 */
export const TeamDomainQueryModelSchema = TeamDomainSelectModelSchema.merge(
  relations({
    avatar: one(FileDomainSelectModelSchema).as<One<FileDomainQueryModel>>(),
    files: many(FileDomainSelectModelSchema).as<Many<FileDomainQueryModel>>(),
    organization: one(OrganizationDomainSelectModelSchema).as<
      One<OrganizationDomainQueryModel>
    >(),
    profile: one(TeamProfileDomainSelectModelSchema).as<
      One<TeamProfileDomainQueryModel>
    >(),
    projects: many(ProjectViewDomainSelectModelSchema).as<
      Many<ProjectDomainQueryModel>
    >(),
    users: many(UserDomainSelectModelSchema).as<Many<UserDomainQueryModel>>(),
    tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
  })
)

export type TeamDomainQueryModel = TeamDomainSelectModel &
  Relations<{
    avatar: One<FileDomainQueryModel>
    files: Many<FileDomainQueryModel>
    organization: One<OrganizationDomainQueryModel>
    profile: One<TeamProfileDomainQueryModel>
    projects: Many<ProjectDomainQueryModel>
    users: Many<UserDomainQueryModel>
    tags: Many<TagDomainQueryModel>
  }>

export const withTeamIds = <TModel extends { teams?: TeamDomainQueryModel[] }>(
  model: TModel
) => ({
  ...model,
  teamIds: model.teams?.map((team) => team.teamId) ?? [],
})

export const hasTeams = <
  TModel extends { teams?: TeamDomainQueryModel[]; teamIds?: TeamId[] },
>({
  teams,
  teamIds,
}: TModel) => (teams && teams.length > 0) || (teamIds && teamIds.length > 0)

export const hasMultipleTeams = <
  TModel extends { teams?: TeamDomainQueryModel[]; teamIds?: TeamId[] },
>({
  teams,
  teamIds,
}: TModel) => (teams && teams.length > 1) || (teamIds && teamIds.length > 1)

export const isMultiTeam = <
  TModel extends {
    teams?: TeamDomainQueryModel[]
    teamIds?: TeamId[]
    organizationType: OrganizationType
  },
>(
  model: TModel
) => hasMultipleTeams(model) || model.organizationType === 'internal'

/**
 * TeamProfileDomainQueryModel
 */
export const TeamProfileDomainQueryModelSchema =
  TeamProfileDomainSelectModelSchema.merge(
    relations({
      team: one(TeamDomainSelectModelSchema).as<One<TeamDomainQueryModel>>(),
    })
  )

export type TeamProfileDomainQueryModel = TeamProfileDomainSelectModel &
  Relations<{
    team: One<TeamDomainQueryModel>
  }>

/**
 * UserDomainQueryModel
 */
export const UserDomainQueryModelSchema = UserDomainSelectModelSchema.merge(
  relations({
    avatar: one(FileDomainSelectModelSchema).as<One<FileDomainQueryModel>>(),
    files: many(FileDomainSelectModelSchema).as<Many<FileDomainQueryModel>>(),
    organization: one(OrganizationDomainSelectModelSchema).as<
      One<OrganizationDomainQueryModel>
    >(),
    tags: many(TagDomainSelectModelSchema).as<Many<TagDomainQueryModel>>(),
    teams: many(TeamDomainSelectModelSchema).as<Many<TeamDomainQueryModel>>(),
  })
)

export type UserDomainQueryModel = UserDomainSelectModel &
  Relations<{
    avatar: One<FileDomainQueryModel>
    files: Many<FileDomainQueryModel>
    organization: One<OrganizationDomainQueryModel>
    tags: Many<TagDomainQueryModel>
    teams: Many<TeamDomainQueryModel>
  }>

/**
 * WatchDomainQueryModel
 */
export const WatchDomainQueryModelSchema = WatchDomainSelectModelSchema.merge(
  relations({
    user: one(UserDomainSelectModelSchema).as<One<UserDomainQueryModel>>(),
  })
)

export type WatchDomainQueryModel = WatchDomainSelectModel &
  Relations<{
    user: One<UserDomainQueryModel>
  }>
